import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';
import {
  getComponentName,
  observeChildren,
} from '@descope-ui/common/components-helpers';
import { injectStyle } from '@descope-ui/common/components-helpers';
import { ListItemClass } from '@descope-ui/descope-list-item/class';
import { createBaseClass } from '@descope-ui/common/base-classes';

export const componentName = getComponentName('list');

class RawList extends createBaseClass({
  componentName,
  baseSelector: '.wrapper',
}) {
  static get observedAttributes() {
    return ['variant', 'readonly'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
    <div class="wrapper">
    <slot></slot>
    <slot name="empty-state">
      No item...
    </slot>
    </div>
	`;

    injectStyle(
      `
    .wrapper {
        overflow: auto;
        display: grid;
        max-height: 100%;
        width: 100%;
      }

      :host {
        display: inline-flex;
        width: 100%;
      }
      slot[name="empty-state"] {
        justify-content: center;
        align-items: center;
        display: flex;
        flex-grow: 1;
      }

      :host slot[name="empty-state"] {
        display: none;
      }
      :host([empty]) slot[name="empty-state"] {
        display: flex;
      }
      ::slotted(:not([slot])) {
        width: 100%;
      }
        `,
      this,
    );
  }

  get items() {
    return this.shadowRoot.querySelector('slot').assignedElements();
  }

  #handleEmptyState() {
    if (this.items.length === 0) {
      this.setAttribute('empty', 'true');
    } else {
      this.removeAttribute('empty');
    }
  }

  get variant() {
    return this.getAttribute('variant') || 'list';
  }

  #handleItemsVariant() {
    this.items.forEach((item) => {
      let listItem = item;
      if (listItem.localName !== ListItemClass.componentName) {
        listItem = item.querySelector(ListItemClass.componentName);
      }

      const listItemVariant = this.variant === 'tiles' ? 'tile' : 'row';
      listItem?.setAttribute('variant', listItemVariant);
    });
  }

  init() {
    super.init?.();

    // we want new items to get the size
    observeChildren(this, () => {
      this.#handleEmptyState();
      this.#handleItemsVariant();
      this.#handleReadOnly();
    });
  }

  get isReadOnly() {
    return this.getAttribute('readonly') === 'true';
  }

  #handleReadOnly() {
    this.items.forEach((item) => {
      if (this.isReadOnly) item.setAttribute('inert', '');
      else item.removeAttribute('inert');
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);

    if (newValue === oldValue) return;

    if (name === 'variant') {
      this.#handleItemsVariant();
    } else if (name === 'readonly') {
      this.#handleReadOnly();
    }
  }
}

export const ListClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { selector: () => ':host', property: 'width' },
      maxHeight: { selector: () => ':host' },
      minHeight: {},
      verticalPadding: [
        { property: 'padding-top' },
        { property: 'padding-bottom' },
      ],
      horizontalPadding: [
        { property: 'padding-left' },
        { property: 'padding-right' },
      ],
      hostDirection: { selector: () => ':host', property: 'direction' },
      fontFamily: {},
      gap: {},

      backgroundColor: {},
      borderRadius: {},
      borderColor: {},
      borderStyle: {},
      borderWidth: {},

      boxShadow: {},
      gridTemplateColumns: {},
      maxItemsWidth: {
        selector: () => '::slotted(:not([slot]))',
        property: 'max-width',
      },
      minItemsWidth: {
        selector: () => '::slotted(:not([slot]))',
        property: 'min-width',
      },
      maxRowItems: {
        property: 'max-row-items',
      },
      itemsHorizontalAlign: {
        selector: () => '::slotted(*)',
        property: 'justify-self',
      },
      emptyStateTextColor: {
        selector: () => 'slot[name="empty-state"]',
        property: 'color',
      },
      emptyStateTextFontFamily: {
        selector: () => 'slot[name="empty-state"]',
        property: 'font-family',
      },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawList);
